"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Rule$ensureConditionType = exports.Rule = void 0;
var property_1 = require("./property");
var type_1 = require("./type");
var rule_invocation_type_1 = require("./rule-invocation-type");
var condition_type_1 = require("./condition-type");
var Rule$customRuleIndex = 0;
var Rule = /** @class */ (function () {
    /**
     * Creates a rule that executes a delegate when specified model events occur.
     * @param rootType The model type the rule is for.
     * @param options The options for the rule.
     */
    function Rule(rootType, name, options) {
        this.invocationTypes = 0;
        this.predicates = [];
        this.returnValues = [];
        // Track the root type
        this.rootType = rootType;
        this.name = name || (options ? options.name : null) || (rootType.fullName + ".Custom." + (++Rule$customRuleIndex));
        // Configure the rule based on the specified options
        if (options) {
            if (options.onInit)
                this.onInit();
            if (options.onInitNew)
                this.onInitNew();
            if (options.onInitExisting)
                this.onInitExisting();
            if (options.onChangeOf)
                this.onChangeOf(options.onChangeOf);
            if (options.returns)
                this.returns(options.returns);
            if (options.execute instanceof Function)
                this._execute = options.execute;
        }
    }
    Object.defineProperty(Rule.prototype, "model", {
        get: function () {
            return this.rootType.model;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Rule.prototype, "eventScope", {
        get: function () {
            return this.rootType.model.eventScope;
        },
        enumerable: false,
        configurable: true
    });
    Rule.prototype.execute = function (entity) {
        if (this._execute) {
            this._execute.call(entity);
        }
        else {
            // TODO: Warn about execute function not implemented?
        }
    };
    // Indicates that the rule should run only for new instances when initialized
    Rule.prototype.onInitNew = function () {
        // ensure the rule has not already been registered
        if (this._registered)
            throw new Error("Rules cannot be configured once they have been registered: " + this.name);
        // configure the rule to run on init new
        this.invocationTypes |= rule_invocation_type_1.RuleInvocationType.InitNew;
        return this;
    };
    // indicates that the rule should run only for existing instances when initialized
    Rule.prototype.onInitExisting = function () {
        // ensure the rule has not already been registered
        if (this._registered)
            throw new Error("Rules cannot be configured once they have been registered: " + this.name);
        // configure the rule to run on init existingh
        this.invocationTypes |= rule_invocation_type_1.RuleInvocationType.InitExisting;
        return this;
    };
    // indicates that the rule should run for both new and existing instances when initialized
    Rule.prototype.onInit = function () {
        // ensure the rule has not already been registered
        if (this._registered)
            throw new Error("Rules cannot be configured once they have been registered: " + this.name);
        // configure the rule to run on both init new and init existing
        this.invocationTypes |= rule_invocation_type_1.RuleInvocationType.InitNew | rule_invocation_type_1.RuleInvocationType.InitExisting;
        return this;
    };
    Rule.prototype.onChangeOf = function (predicates) {
        // ensure the rule has not already been registered
        if (this._registered)
            throw new Error("Rules cannot be configured once they have been registered: " + this.name);
        // allow change of predicates to be specified as a parameter array without []'s
        if (!(predicates instanceof Array)) {
            predicates = Array.prototype.slice.call(arguments);
        }
        // add to the set of existing change predicates
        this.predicates = this.predicates.length > 0 ? this.predicates.concat(predicates) : predicates;
        // also configure the rule to run on property change unless it has already been configured to run on property get
        if ((this.invocationTypes & rule_invocation_type_1.RuleInvocationType.PropertyGet) === 0)
            this.invocationTypes |= rule_invocation_type_1.RuleInvocationType.PropertyChanged;
        return this;
    };
    Rule.prototype.returns = function (properties) {
        // Ensure the rule has not already been registered
        if (this._registered)
            throw new Error("Rules cannot be configured once they have been registered: " + this.name);
        // Allow return properties to be specified as a parameter array without []'s
        if (properties && properties.constructor === String)
            properties = Array.prototype.slice.call(arguments);
        if (!properties)
            throw new Error("Rule must specify at least one property for returns.");
        // Add to the set of existing return value properties
        this.returnValues = this.returnValues.length > 0 ? this.returnValues.concat(properties) : properties;
        // Configure the rule to run on property get and not on property change
        this.invocationTypes |= rule_invocation_type_1.RuleInvocationType.PropertyGet;
        this.invocationTypes &= ~rule_invocation_type_1.RuleInvocationType.PropertyChanged;
        return this;
    };
    // registers the rule based on the configured invocation types, predicates, and return values
    Rule.prototype.register = function () {
        var rule = this;
        if (rule._registered) {
            throw new Error("Rules cannot be registered more than once: " + rule.name);
        }
        // Indicate that the rule should now be considered registered and cannot be reconfigured
        Object.defineProperty(this, "_registered", { enumerable: false, value: true, writable: false });
        // register for init new
        if (rule.invocationTypes & rule_invocation_type_1.RuleInvocationType.InitNew) {
            rule.rootType.initNew.subscribe(function (args) { executeRule(rule, args.entity); });
        }
        // register for init existing
        if (rule.invocationTypes & rule_invocation_type_1.RuleInvocationType.InitExisting) {
            rule.rootType.initExisting.subscribe(function (args) { executeRule(rule, args.entity); });
        }
        // register for property change
        if (rule.invocationTypes & rule_invocation_type_1.RuleInvocationType.PropertyChanged) {
            rule.predicates.forEach(function (predicate) {
                predicate.changed.subscribe(function (args) {
                    if (canExecuteRule(rule, args.entity) && !pendingInvocation(args.entity.meta, rule)) {
                        pendingInvocation(args.entity.meta, rule, true);
                        rule.eventScope.onExit(function (e) {
                            pendingInvocation(args.entity.meta, rule, false);
                            if (!e.abort)
                                executeRule(rule, args.entity);
                        });
                    }
                });
            });
        }
        // register for property get
        if (rule.invocationTypes & rule_invocation_type_1.RuleInvocationType.PropertyGet && rule.returnValues) {
            // register for property get events for each return value to calculate the property when accessed
            rule.returnValues.forEach(function (returnValue) {
                returnValue.accessed.subscribe(function (args) {
                    // run the rule to initialize the property if it is pending initialization
                    if (canExecuteRule(rule, args.entity) && property_1.Property$pendingInit(args.entity, returnValue)) {
                        property_1.Property$pendingInit(args.entity, returnValue, false);
                        executeRule(rule, args.entity);
                    }
                });
            });
            // register for property change events for each predicate to invalidate the property value when inputs change
            rule.predicates.forEach(function (predicate) {
                predicate.changed.subscribe(function (args) {
                    if (rule.returnValues.some(function (returnValue) { return returnValue.changed.hasSubscribers(); })) {
                        // Immediately execute the rule if there are explicit event subscriptions for the property
                        if (canExecuteRule(rule, args.entity) && !pendingInvocation(args.entity.meta, rule)) {
                            pendingInvocation(args.entity.meta, rule, true);
                            rule.eventScope.onExit(function (e) {
                                pendingInvocation(args.entity.meta, rule, false);
                                if (!e.abort)
                                    executeRule(rule, args.entity);
                            });
                        }
                    }
                    else {
                        // Otherwise, just mark the property as pending initialization and raise property change for UI subscribers
                        rule.returnValues.forEach(function (returnValue) {
                            property_1.Property$pendingInit(args.entity, returnValue, true);
                        });
                        // Defer change notification until the scope of work has completed
                        rule.eventScope.onExit(function (e) {
                            if (!e.abort) {
                                rule.returnValues.forEach(function (returnValue) {
                                    args.entity.changed.publish(args.entity, { entity: args.entity, property: returnValue, newValue: returnValue.value(args.entity) });
                                });
                            }
                        });
                    }
                });
            });
        }
    };
    return Rule;
}());
exports.Rule = Rule;
function pendingInvocation(target, rule, value) {
    if (value === void 0) { value = null; }
    var pendingInvocation;
    if (Object.prototype.hasOwnProperty.call(target, "__pendingInvocation__")) {
        pendingInvocation = target.__pendingInvocation__;
    }
    else {
        Object.defineProperty(target, "__pendingInvocation__", { enumerable: false, value: (pendingInvocation = []), writable: true });
    }
    var indexOfRule = pendingInvocation.indexOf(rule);
    if (arguments.length > 2) {
        if (value && indexOfRule < 0) {
            pendingInvocation.push(rule);
        }
        else if (!value && indexOfRule >= 0) {
            pendingInvocation.splice(indexOfRule, 1);
        }
    }
    else {
        return indexOfRule >= 0;
    }
}
function canExecuteRule(rule, obj) {
    // ensure the rule target is a valid rule root type
    return obj instanceof rule.rootType.jstype;
}
function executeRule(rule, obj) {
    // Ensure that the rule can be executed.
    if (!canExecuteRule(rule, obj)) {
        // TODO: Warn that rule can't be executed?
        return;
    }
    try {
        rule.eventScope.perform(function () {
            rule.execute(obj);
        });
    }
    catch (e) {
        console.warn("Error encountered while running rule \"" + rule.name + "\".");
        if (e)
            console.warn(e);
    }
}
;
function Rule$ensureConditionType(ruleName, typeOrProp, category) {
    var generatedCode = typeOrProp instanceof property_1.Property ? typeOrProp.containingType.fullName + "." + typeOrProp.name + "." + ruleName :
        typeOrProp instanceof type_1.Type ? typeOrProp + "." + ruleName :
            ruleName;
    var counter = "";
    while (condition_type_1.ConditionType.get(generatedCode + counter))
        counter = (typeof counter === "string" ? 0 : counter) + 1;
    var DesiredConditionType;
    if (category === "Error") {
        DesiredConditionType = condition_type_1.ErrorConditionType;
    }
    else if (category === "Warning") {
        DesiredConditionType = condition_type_1.WarningConditionType;
    }
    else {
        throw new Error("Cannot create condition type for unsupported category '" + category + "'.");
    }
    // return a new client condition type of the specified category
    return new DesiredConditionType(generatedCode + counter, "Generated condition type for " + ruleName + " rule.");
}
exports.Rule$ensureConditionType = Rule$ensureConditionType;
